/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Session storage helpers for Directory list restore after profile navigation.
 * @module utils/directory/directoryListSession
 */

import { logger, sanitizeError } from "../logger.js";

export const DIRECTORY_LIST_SESSION_KEY = "caico.directory.listState";
export const DIRECTORY_RESTORE_MAX_AGE_MS = 15 * 60 * 1000;

export const readDirectoryListSession = () => {
  try {
    const raw = sessionStorage.getItem(DIRECTORY_LIST_SESSION_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const writeDirectoryListSession = (data) => {
  try {
    sessionStorage.setItem(DIRECTORY_LIST_SESSION_KEY, JSON.stringify(data));
  } catch (error) {
    logger.warn("Could not persist directory list state:", {
      err: sanitizeError(error),
    });
  }
};

export const clearDirectoryListSession = () => {
  try {
    sessionStorage.removeItem(DIRECTORY_LIST_SESSION_KEY);
  } catch {
    // Session storage unavailable.
  }
};

export const isValidDirectoryRestoreSession = (saved) =>
  Boolean(
    saved?.awaitingRestore &&
      saved.v === 1 &&
      Date.now() - (saved.savedAt ?? 0) <= DIRECTORY_RESTORE_MAX_AGE_MS,
  );
